// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

const SW_UPDATE_EVENT = 'swUpdate';
const LEGACY_SW_UPDATE_EVENT = 'sw-update-available';

function dispatchSWUpdateEvent(registration, waiting) {
  const waitingWorker = waiting || registration?.waiting || null;
  const detail = { registration, waiting: waitingWorker };

  window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT, { detail }));
  // 向後兼容舊事件名稱
  window.dispatchEvent(new CustomEvent(LEGACY_SW_UPDATE_EVENT, { detail }));
}

export function register(config) {
  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    const startRegistration = () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    };

    // Cloudflare Rocket Loader 等優化工具可能令主 bundle 在 load 之後才執行。
    // 若只綁 load 事件，會錯過註冊時機，導致 SW 無法註冊。
    if (document.readyState === 'complete') {
      startRegistration();
    } else {
      window.addEventListener('load', startRegistration, { once: true });
    }
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // 檢查是否已經有等待中的 Service Worker（頁面加載時）
      if (registration.waiting) {
        console.log('Service Worker update already waiting');
        dispatchSWUpdateEvent(registration, registration.waiting);
      }

      // 監聽 Service Worker 更新
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // 等待一小段時間確保 waiting worker 已設置
              setTimeout(() => {
                const waitingWorker = registration.waiting || installingWorker;
                console.log('Dispatching update event, waiting worker:', waitingWorker);

                // 發送自定義事件通知 App 有新版本可用
                dispatchSWUpdateEvent(registration, waitingWorker);
              }, 100);

              // Execute callback (向後兼容)
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // 定期檢查更新（每小時檢查一次）
      setInterval(() => {
        registration.update();
      }, 60 * 60 * 1000);

      // 立即檢查一次更新（頁面加載時）
      registration.update();
    })
    .catch(error => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
      // 即使沒有網絡，也嘗試註冊 Service Worker（可能已有緩存）
      registerValidSW(swUrl, config);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}
